import type { Item, PlaybackLink, TextSlice } from '@mystiq/contracts';
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { sameOrigin } from '../api/client.ts';
import { useApi } from '../auth/session.tsx';
import { clockTime } from '../lib/format.ts';
import { ErrorLine } from './ui.tsx';

/**
 * Plays a recording from a short-lived link that serves byte ranges, so it
 * can be sought. When the link runs out (a long meeting, a tab left paused),
 * a fresh one is fetched and playback continues from the same moment.
 */
export function RecordingPlayer({ ws, item, media, onTime }: { ws: string; item: Item; media: RefObject<HTMLMediaElement | null>; onTime(ms: number): void }) {
  const api = useApi();
  const [link, setLink] = useState<PlaybackLink | null>(null);
  const [error, setError] = useState<unknown>(null);
  const resume = useRef<{ at: number; playing: boolean } | null>(null);

  const refresh = useCallback(async () => {
    const element = media.current;
    if (element && element.currentSrc) resume.current = { at: element.currentTime, playing: !element.paused };
    try {
      setLink(await api.playbackLink(ws, item.id));
      setError(null);
    } catch (caught) {
      setError(caught);
    }
  }, [api, ws, item.id, media]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Shortly before the link runs out: a new one, now if paused, otherwise when playback next stops.
  useEffect(() => {
    if (!link) return;
    const timer = setTimeout(() => {
      if (media.current?.paused !== false) void refresh();
    }, Math.max(5_000, Date.parse(link.expiresAt) - Date.now() - 60_000));
    return () => clearTimeout(timer);
  }, [link, refresh, media]);

  const restore = () => {
    const element = media.current;
    const at = resume.current;
    if (!element || !at) return;
    resume.current = null;
    element.currentTime = at.at;
    if (at.playing) void element.play().catch(() => undefined);
  };
  const common = {
    controls: true,
    preload: 'metadata' as const,
    src: link ? sameOrigin(link.url) : undefined,
    onLoadedMetadata: restore,
    onTimeUpdate: () => onTime((media.current?.currentTime ?? 0) * 1000),
    // An expired link fails like a lost connection: get a fresh one and carry on.
    onError: () => {
      if (link && Date.parse(link.expiresAt) - Date.now() < 120_000) void refresh();
      else if (link) setError(new Error('This recording could not be played in this browser. Download it to play it elsewhere.'));
    },
  };
  // Known once transcribed. Before that, recordings made here are sound only; an upload may be a video.
  const video = item.media ? item.media.video : item.capture === null && link?.media === 'video';
  return (
    <div className="stack">
      {video ? (
        <div className="video-stage">
          <video ref={media as RefObject<HTMLVideoElement>} aria-label={`Play ${item.displayTitle}`} {...common} />
        </div>
      ) : (
        <audio ref={media as RefObject<HTMLAudioElement>} className="audio-player" aria-label={`Play ${item.displayTitle}`} {...common} />
      )}
      <ErrorLine error={error} />
    </div>
  );
}

/**
 * A transcript as heard: each passage with the moment it starts, which plays
 * the recording from there. The passage playing now is marked. Text edited
 * after transcription has no timings, and is shown without them.
 */
export function TimedTranscript({ slice, playingMs, onSeek }: { slice: TextSlice; playingMs: number; onSeek(ms: number): void }) {
  if (!slice.timings.length) return <div className="transcript-text">{slice.text}</div>;
  return (
    <ol className="transcript" aria-label="Transcript">
      {slice.timings.map((timing) => {
        const now = playingMs >= timing.startMs && playingMs < timing.endMs;
        return (
          <li key={`${timing.start}-${timing.startMs}`} aria-current={now ? 'true' : undefined}>
            <button className="link-button transcript-time" aria-label={`Play from ${clockTime(timing.startMs)}`} onClick={() => onSeek(timing.startMs)}>
              {clockTime(timing.startMs)}
            </button>
            <p className="transcript-passage">{slice.text.slice(timing.start - slice.offset, timing.end - slice.offset)}</p>
          </li>
        );
      })}
    </ol>
  );
}
